import { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Fade,
  Popper,
  ClickAwayListener,
  Divider,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MicOffIcon from "@mui/icons-material/MicOff";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { useTranslation } from "react-i18next";


interface DescriptionVoiceRecorderProps {
  onTranscriptChange: (transcript: string) => void;
  onAudioAttachment?: (audioBlob: Blob, transcript: string) => void;
  onAIImprove?: (transcript: string) => Promise<string>;
  onRecordingChange?: (isRecording: boolean) => void;
  onAIProcessingChange?: (isProcessing: boolean) => void;
  onFinalizingChange?: (isFinalizing: boolean) => void;
  onProcessingMessageChange?: (message: string) => void;
  language?: string;
  disabled?: boolean;
  maxLength?: number;
  currentText?: string;
  currentHtmlValue?: string;
  onHtmlRestore?: (html: string) => void;
}

export const DescriptionVoiceRecorder: React.FC<DescriptionVoiceRecorderProps> = ({
  onTranscriptChange,
  onAudioAttachment,
  onAIImprove,
  onRecordingChange,
  onAIProcessingChange,
  onFinalizingChange,
  onProcessingMessageChange,
  language = "en-US",
  disabled = false,
  maxLength,
  currentText = "",
  currentHtmlValue,
  onHtmlRestore,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [recordedTranscript, setRecordedTranscript] = useState("");
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showAIImproveOnly, setShowAIImproveOnly] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isRecordingRef = useRef(false);
  const currentTextRef = useRef(currentText);
  const maxLengthRef = useRef(maxLength);
  const onTranscriptChangeRef = useRef(onTranscriptChange);
  const textBeforeRecordingRef = useRef(currentText);
  const transcriptDuringRecordingRef = useRef("");
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const finalizingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isStoppingRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    currentTextRef.current = currentText;
  }, [currentText]);

  useEffect(() => {
    maxLengthRef.current = maxLength;
  }, [maxLength]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    onAIProcessingChange?.(isAIProcessing);
  }, [isAIProcessing, onAIProcessingChange]);

  useEffect(() => {
    onFinalizingChange?.(isFinalizing);
  }, [isFinalizing, onFinalizingChange]);

  useEffect(() => {
    onTranscriptChangeRef.current = onTranscriptChange;
  }, [onTranscriptChange]);

  // Update parent with processing message
  useEffect(() => {
    onProcessingMessageChange?.(processingMessage);
  }, [processingMessage, onProcessingMessageChange]);

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    // Initialize speech recognition only once
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;

    recognition.onresult = (event: any) => {
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        }
      }

      // Store transcript during recording and update immediately
      if (final) {
        transcriptDuringRecordingRef.current += final;

        // Update text immediately
        const newText = currentTextRef.current + final;
        if (!maxLengthRef.current || newText.length <= maxLengthRef.current) {
          currentTextRef.current = newText;
          onTranscriptChangeRef.current(newText);
        }
      }
    };

    recognition.onerror = (event: any) => {
      // Handle expected errors silently
      if (event.error === "no-speech") {
        return;
      }
      if (event.error === "aborted") {
        return;
      }

      // Log unexpected errors
      console.error("Speech recognition error:", event.error);
      setIsRecording(false);
      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage("");
      isStoppingRef.current = false;

      // Clear timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
    };

    recognition.onend = () => {
      if (isRecordingRef.current) {
        try {
          recognition.start();
        } catch (e) {
          console.error("Error restarting recognition:", e);
          setIsRecording(false);
          setIsFinalizing(false);
          setIsProcessingSpeech(false);
          setProcessingMessage("");
          isStoppingRef.current = false;

          // Clear timeouts
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
          }
          if (finalizingTimeoutRef.current) {
            clearTimeout(finalizingTimeoutRef.current);
          }
        }
      } else if (isStoppingRef.current) {
        // Recognition ended after user stopped - wait for final results then cleanup
        if (finalizingTimeoutRef.current) {
          clearTimeout(finalizingTimeoutRef.current);
        }
        finalizingTimeoutRef.current = setTimeout(() => {
          const transcript = transcriptDuringRecordingRef.current;
          setRecordedTranscript(transcript);
          setIsFinalizing(false);
          setIsProcessingSpeech(false);
          setProcessingMessage("");
          setShowOptions(true);
          isStoppingRef.current = false;
        }, 800);
      } else {
        // Normal end, clear finalizing state
        setIsFinalizing(false);
        setIsProcessingSpeech(false);
        setProcessingMessage("");
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.error("Error aborting recognition:", e);
        }
      }
    };
  }, [language]);

  const startRecording = async () => {
    if (!recognitionRef.current || disabled) return;

    try {
      // Clear any existing timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }

      // Request microphone access for audio recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Initialize MediaRecorder for audio capture
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      transcriptDuringRecordingRef.current = "";

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setRecordedAudioBlob(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;

      // Save current text and start speech recognition
      currentTextRef.current = currentText;
      textBeforeRecordingRef.current = currentText;
      recognitionRef.current.start();
      setIsRecording(true);
      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage(t("Listening..."));
    } catch (error) {
      console.error("Error starting recording:", error);
      setProcessingMessage("");
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = true;

      setIsFinalizing(true);
      setProcessingMessage(t("voiceRecorderFinalizing"));

      // Now stop the recognition
      recognitionRef.current.stop();

      // Stop audio recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      // Safety timeout - prevent infinite loader (10 seconds max)
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }
      finalizingTimeoutRef.current = setTimeout(() => {
        const transcript = transcriptDuringRecordingRef.current;
        setRecordedTranscript(transcript);
        setIsFinalizing(false);
        setProcessingMessage("");
        setShowOptions(true);
        isStoppingRef.current = false;
      }, 10000);
    } catch (error) {
      console.error("Error stopping recording:", error);
      setIsFinalizing(false);
      setProcessingMessage("");
      isStoppingRef.current = false;
    }
  };

  const cancelRecording = () => {
    if (!recognitionRef.current) return;

    try {
      // Update state BEFORE stopping to prevent restart in onend handler
      setIsRecording(false);
      isRecordingRef.current = false;
      isStoppingRef.current = false;

      // Now stop the recognition
      recognitionRef.current.stop();

      setIsFinalizing(false);
      setIsProcessingSpeech(false);
      setProcessingMessage("");

      // Clear timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (finalizingTimeoutRef.current) {
        clearTimeout(finalizingTimeoutRef.current);
      }

      // Stop and discard audio recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      audioChunksRef.current = [];
      transcriptDuringRecordingRef.current = "";
    } catch (error) {
      console.error("Error canceling recording:", error);
      setProcessingMessage("");
    }
  };

  const handleAIImprove = async () => {
    try {
      setIsAIProcessing(true);
      setProcessingMessage(t("AI is improving your text..."));

      // Get all current text from description (use currentText prop which is up-to-date)
      const allCurrentText = currentText.trim();

      // Save the current HTML value to undo stack to preserve formatting
      setUndoStack((prev) => [...prev, currentHtmlValue || allCurrentText]);
      // Clear redo stack when new action is performed
      setRedoStack([]);

      let improvedText = allCurrentText;

      // Call AI improvement with HTML content to preserve formatting context
      if (onAIImprove && allCurrentText) {
        try {
          // Send HTML content if available so AI understands the structure/formatting
          const textForAI = currentHtmlValue || allCurrentText;
          const promptWithContext = `Improve the task description and keep the HTML format in your response: "${textForAI}"`;
          improvedText = await onAIImprove(promptWithContext);
          improvedText = improvedText || allCurrentText;
        } catch (error) {
          console.error("Error improving text with AI:", error);
          improvedText = allCurrentText;
        }
      }

      // Replace all text with improved text
      // Use onHtmlRestore if available so HTML content from AI is rendered properly
      if (!maxLengthRef.current || improvedText.length <= maxLengthRef.current) {
        if (onHtmlRestore) {
          onHtmlRestore(improvedText);
        } else {
          onTranscriptChangeRef.current(improvedText);
        }
      }

      // Reset immediately for better UX
      resetRecording();
    } catch (error) {
      console.error("Error in handleAIImprove:", error);
      resetRecording();
    } finally {
      setIsAIProcessing(false);
      setProcessingMessage("");
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;

    // Get the last value from undo stack (may be HTML)
    const previousValue = undoStack[undoStack.length - 1];
    const newUndoStack = undoStack.slice(0, -1);

    // Save current HTML value to redo stack to preserve formatting
    setRedoStack((prev) => [...prev, currentHtmlValue || currentText]);
    setUndoStack(newUndoStack);

    // Restore previous value - use HTML restore if available to preserve formatting
    if (onHtmlRestore) {
      onHtmlRestore(previousValue);
    } else {
      onTranscriptChangeRef.current(previousValue);
    }
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;

    // Get the last value from redo stack (may be HTML)
    const nextValue = redoStack[redoStack.length - 1];
    const newRedoStack = redoStack.slice(0, -1);

    // Save current HTML value to undo stack to preserve formatting
    setUndoStack((prev) => [...prev, currentHtmlValue || currentText]);
    setRedoStack(newRedoStack);

    // Restore next value - use HTML restore if available to preserve formatting
    if (onHtmlRestore) {
      onHtmlRestore(nextValue);
    } else {
      onTranscriptChangeRef.current(nextValue);
    }
  };

  const handleAudioSave = async () => {
    try {
      // Text is already added, just attach audio
      if (recordedAudioBlob && onAudioAttachment) {
        onAudioAttachment(recordedAudioBlob, recordedTranscript);
      }

      // Reset immediately for better UX
      resetRecording();
    } catch (error) {
      console.error("Error in handleAudioSave:", error);
      resetRecording();
    }
  };

  const resetRecording = () => {
    setRecordedTranscript("");
    setRecordedAudioBlob(null);
    setShowOptions(false);
    setShowAIImproveOnly(false);
    setAnchorEl(null);
    // Don't clear undo/redo stacks here - keep them for undo/redo functionality
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Show options on hover if there's text or recorded audio, and not currently AI processing or recording
    if (!isAIProcessing && !isRecording && (showOptions || showAIImproveOnly || currentText.trim())) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    // Don't close if AI is processing
    if (!isAIProcessing) {
      setAnchorEl(null);
    }
  };

  // Check if there's existing text to show AI Improve option
  useEffect(() => {
    if (!isRecording && !showOptions && currentText.trim()) {
      setShowAIImproveOnly(true);
    } else if (!currentText.trim() && !showOptions) {
      setShowAIImproveOnly(false);
    }
  }, [currentText, isRecording, showOptions]);

  if (!isSupported) {
    return (
      <Tooltip title={t("Speech recognition not supported")}>
        <IconButton size="small" disabled>
          <MicOffIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      {isRecording ? (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {/* Cancel button */}
          <Tooltip title={t("Cancel recording")}>
            <IconButton
              onClick={cancelRecording}
              size="small"
              sx={{
                width: "20px",
                height: "20px",
                backgroundColor: (theme) => theme.palette.grey[400],
                color: "white",
                padding: 0,
                minWidth: "20px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[500],
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
          {/* Save button */}
          <Tooltip title={t("Stop recording")}>
            <IconButton
              onClick={stopRecording}
              size="small"
              sx={{
                width: "20px",
                height: "20px",
                backgroundColor: (theme) => theme.palette.grey[600],
                color: "white",
                padding: 0,
                minWidth: "20px",
                zIndex: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.grey[700],
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 14, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.3,
          }}
        >
          {/* Undo/Redo Group - Compact spacing, only visible when history exists */}
          {(undoStack.length > 0 || redoStack.length > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.2,
                borderRight: "1px solid",
                borderColor: "divider",
                pr: 0.5,
                mr: 0.3,
              }}
            >
              {/* Undo Icon */}
              <Tooltip title={undoStack.length > 0 ? t("Undo") : t("No actions to undo")}>
                <span>
                  <IconButton
                    onClick={handleUndo}
                    disabled={undoStack.length === 0}
                    size="small"
                    sx={{
                      padding: "4px",
                      minWidth: 0,
                      color: (theme) =>
                        undoStack.length > 0
                          ? theme.palette.text.secondary
                          : theme.palette.action.disabled,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          undoStack.length > 0
                            ? theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                      },
                      "&.Mui-disabled": {
                        color: (theme) => theme.palette.action.disabled,
                      },
                    }}
                  >
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Redo Icon */}
              <Tooltip title={redoStack.length > 0 ? t("Redo") : t("No actions to redo")}>
                <span>
                  <IconButton
                    onClick={handleRedo}
                    disabled={redoStack.length === 0}
                    size="small"
                    sx={{
                      padding: "4px",
                      minWidth: 0,
                      color: (theme) =>
                        redoStack.length > 0
                          ? theme.palette.text.secondary
                          : theme.palette.action.disabled,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          redoStack.length > 0
                            ? theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                      },
                      "&.Mui-disabled": {
                        color: (theme) => theme.palette.action.disabled,
                      },
                    }}
                  >
                    <RedoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* AI Improve Icon - always visible, disabled when no text */}
          <Tooltip title={currentText.trim() ? t("AI Improve") : t("Add text to improve")} arrow placement="top">
            <span>
              <IconButton
                onClick={handleAIImprove}
                disabled={!currentText.trim()}
                size="small"
                sx={{
                  padding: "4px",
                  minWidth: 0,
                  color: (theme) => currentText.trim() ? theme.palette.primary.main : theme.palette.action.disabled,
                  "&:hover": {
                    backgroundColor: (theme) =>
                      currentText.trim() ? "primary.50" : "transparent",
                  },
                  "&.Mui-disabled": {
                    color: (theme) => theme.palette.action.disabled,
                  },
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Voice Icon with Save Audio on hover */}
          <Box
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.3,
            }}
          >
            {/* Save Audio Icon - only show on hover when there's recorded audio */}
            {Boolean(anchorEl) && showOptions && recordedAudioBlob && (
              <Fade in={Boolean(anchorEl)} timeout={200}>
                <Tooltip title={t("Save Audio")} arrow placement="top">
                  <IconButton
                    onClick={handleAudioSave}
                    size="small"
                    sx={{
                      padding: "4px",
                      minWidth: 0,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(0, 0, 0, 0.04)",
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "rgba(0, 0, 0, 0.08)",
                      },
                    }}
                  >
                    <SaveIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              </Fade>
            )}

            <Tooltip title={t("Click to record voice")}>
              <IconButton
                onClick={startRecording}
                disabled={disabled}
                size="small"
                sx={{
                  padding: "4px",
                  minWidth: 0,
                  color: (theme) => theme.palette.text.secondary,
                  position: "relative",
                  "&:hover": {
                    color: (theme) => theme.palette.primary.main,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                  ...(showOptions && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "success.main",
                      top: 4,
                      right: 4,
                    },
                  }),
                }}
              >
                <MicIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </>
  );
};

export default DescriptionVoiceRecorder;
